export const RUBBER_CODES = [
  "1x2",
  "2x1",
  "3x4",
  "4x3",
  "D1",
  "D2",
  "1x1",
  "2x2",
  "3x3",
  "4x4",
];

export const CORE_POINT_TAGS = [
  "point_start",
  "amazing_rally",
  "service_missed",
  "service_ace",
  "service_attack_3rd",
  "unforced_error",
];

export const EXTRA_POINT_TAGS = [
  "forced_error",
  "winner",
  "winner_open_play",
  "return_winner",
  "return_error",
  "lucky",
  "rally",
  "net",
  "edge",
  "let_serve",
  "timeout_on",
  "timeout_off",
  "set_point",
  "match_point",
];

export const TIMELINE_MARKER_TAGS = [
  "point_start",
  "timeout_on",
  "timeout_off",
  "let_serve",
];

export const GAME_SCHEMAS = [
  "court_coupe",
  "long_coupe",
  "long_rapide",
  "court_lateral",
  "long_varie",
];

export function setCells(score) {
  const completed = Array.isArray(score?.completed) ? score.completed : [];
  const cells = [0, 1, 2, 3, 4].map((i) => completed[i] || null);
  if (!score || score.home_sets >= 3 || score.away_sets >= 3) return cells;
  const liveH = Number(score.live_home) || 0;
  const liveA = Number(score.live_away) || 0;
  const last = completed[completed.length - 1];
  const liveIsHeldFinal =
    Boolean(score.awaiting_next_set) ||
    (last && last.home === liveH && last.away === liveA && (liveH || liveA));
  if ((liveH || liveA) && !liveIsHeldFinal) {
    const idx = completed.length;
    if (idx < 5) cells[idx] = { home: liveH, away: liveA, live: true };
  }
  return cells;
}

export function formatSetCell(cell) {
  if (!cell) return "";
  return `${cell.home}–${cell.away}`;
}

export function lastPointForRubber(points, rubberId) {
  const list = (points || []).filter((p) => p.rubber_id === rubberId);
  return list.length ? list[list.length - 1] : null;
}

export function formatTms(tMs) {
  if (tMs == null || !Number.isFinite(Number(tMs))) return "";
  const total = Math.max(0, Math.floor(Number(tMs) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Scorecam is for board OCR / setup only — never the match review tape. */
export function isScorecamMedia(media) {
  const raw = String(media?.angle_label || media?.camera_role || "")
    .toLowerCase()
    .trim();
  return raw === "scorecam" || raw === "score" || raw === "score_camera";
}

/** Media shown in the spectator/referee review player. */
export function matchPlaybackMedia(media) {
  return (media || []).filter((item) => !isScorecamMedia(item));
}

/**
 * Scorecam clips for moderator listing.
 * When `rubberId` is set, only clips stamped to that rubber (never unassigned
 * table fallbacks — those must not appear in rubber video review).
 * Omit `rubberId` for match-level listing.
 */
export function scorecamDebugMedia(media, { rubberId, tableNumber } = {}) {
  return (media || [])
    .filter((item) => {
      if (!isScorecamMedia(item) || item?.kind !== "clip" || !item.file_url) {
        return false;
      }
      if (rubberId) {
        return (
          item.rubber_id != null &&
          String(item.rubber_id) === String(rubberId)
        );
      }
      if (
        tableNumber != null &&
        item.table_number != null &&
        Number(item.table_number) !== Number(tableNumber)
      ) {
        return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => {
      const dt = (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0);
      if (dt !== 0) return dt;
      return (
        (Date.parse(a.captured_at || a.created_at || 0) || 0) -
        (Date.parse(b.captured_at || b.created_at || 0) || 0)
      );
    });
}

/**
 * Clip / point / OCR timestamps share one axis: ms since media_sync_at
 * (wall-clock atomic epoch for the open set capture).
 */
export function seekSecondsForPoint(point, clip) {
  const marks = Array.isArray(clip?.point_marks) ? clip.point_marks : [];
  if (point?.point_id != null && marks.length) {
    const hit = marks.find(
      (mark) => String(mark?.point_id) === String(point.point_id)
    );
    if (hit && Number.isFinite(Number(hit.start_sec))) {
      return Math.max(0, Number(hit.start_sec));
    }
  }
  if (point?.t_ms == null || !Number.isFinite(Number(point.t_ms))) return null;
  const pointT = Number(point.t_ms);
  const clipT =
    clip?.t_ms != null && Number.isFinite(Number(clip.t_ms))
      ? Number(clip.t_ms)
      : 0;
  return Math.max(0, (pointT - clipT) / 1000);
}

/**
 * Clips for the set currently on screen.
 * A merged set video (point_marks) wins. Until that file exists, play the
 * set's point chunks in order so a tag still opens that point.
 */
export function setReviewSources(
  media,
  { rubberId, setNumber, setPoints = [], allPoints = null } = {}
) {
  const clips = matchPlaybackMedia(media).filter(
    (item) => item?.kind === "clip" && item.file_url
  );
  const marked = clips.filter((item) => {
    if (!Array.isArray(item.point_marks) || !item.point_marks.length) return false;
    if (setNumber != null && Number(item.set_number) !== Number(setNumber)) return false;
    if (
      rubberId &&
      item.rubber_id &&
      String(item.rubber_id) !== String(rubberId)
    ) {
      return false;
    }
    return true;
  });
  if (marked.length) return [marked[0]];

  // One file for the whole match (no rubber stamp). Point marks still seek.
  const matchTape = clips.find(
    (item) =>
      !item.rubber_id &&
      Array.isArray(item.point_marks) &&
      item.point_marks.length
  );
  if (matchTape) return [matchTape];

  const roster = (allPoints || setPoints || []).filter(
    (point) =>
      !rubberId ||
      !point?.rubber_id ||
      String(point.rubber_id) === String(rubberId)
  );
  const looseChunks = looseMatchChunks(media, rubberId);
  if (looseChunks.length >= 2 && roster.length) {
    const stitched = stitchMatchRecordingChunks(looseChunks, {
      rubberId,
      setNumber,
      points: roster,
    });
    // Tagged matches become one tape per set. Do not fall through onto
    // another rubber's clips.
    if (stitched.length) return stitched;
  }
  // Untagged (e.g. FLTT sheet sync): play the best single capture on this
  // rubber. Do not mash unrelated full uploads into one multi-part tape.
  if (looseChunks.length && !roster.length) {
    const groups = groupChunksByCapture(looseChunks);
    groups.sort((a, b) => {
      const score = (group) => {
        const segs = group.segs || [];
        const span = tapeSpanMs(segs);
        // Multi-chunk capture timelines beat single leftovers; span beats count.
        return span + segs.length * 60_000;
      };
      const diff = score(b) - score(a);
      if (diff !== 0) return diff;
      // Prefer recovered full takes over short end-of-night stubs.
      const rankKey = (group) => {
        const id = String(group.captureId || "");
        if (id.startsWith("recov")) return 2;
        if (id.startsWith("cap")) return 0;
        return 1;
      };
      const rk = rankKey(b) - rankKey(a);
      if (rk !== 0) return rk;
      // Earlier start ≈ full match; late stubs are usually short.
      const ta = Date.parse(a.segs[0]?.captured_at || a.segs[0]?.created_at || 0) || 0;
      const tb = Date.parse(b.segs[0]?.captured_at || b.segs[0]?.created_at || 0) || 0;
      return ta - tb;
    });
    const best = groups[0]?.segs || [];
    if (best.length) return best;
  }

  const setNum = Number(setNumber);
  const stamped = clips.filter((item) => {
    if (!Number.isFinite(setNum) || Number(item.set_number) !== setNum) return false;
    if (
      rubberId &&
      item.rubber_id &&
      String(item.rubber_id) !== String(rubberId)
    ) {
      return false;
    }
    return true;
  });

  const ids = new Set((setPoints || []).map((point) => String(point.point_id)));
  const newest = new Map();
  for (const clip of clips) {
    const id = clip.point_id != null ? String(clip.point_id) : "";
    if (!id || !ids.has(id)) continue;
    const prev = newest.get(id);
    const at = Date.parse(clip.created_at || clip.captured_at || 0) || 0;
    const prevAt = prev
      ? Date.parse(prev.created_at || prev.captured_at || 0) || 0
      : -1;
    if (!prev || at >= prevAt) newest.set(id, clip);
  }
  const ordered = [];
  for (const point of setPoints || []) {
    const clip = newest.get(String(point.point_id));
    if (clip) ordered.push(clip);
  }
  if (ordered.length) return ordered;
  if (stamped.length) {
    return stamped
      .slice()
      .sort((a, b) => (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0));
  }

  // A rubber clip with no set stamp (one file for the whole game) still plays.
  const rubberClips = clips.filter((item) => {
    if (!rubberId || !item.rubber_id) return false;
    if (String(item.rubber_id) !== String(rubberId)) return false;
    if (
      item.set_number != null &&
      Number.isFinite(setNum) &&
      Number(item.set_number) !== setNum
    ) {
      return false;
    }
    return true;
  });
  return rubberClips.slice().sort((a, b) => {
    const dt = (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0);
    if (dt !== 0) return dt;
    return (
      (Date.parse(a.captured_at || a.created_at || 0) || 0) -
      (Date.parse(b.captured_at || b.created_at || 0) || 0)
    );
  });
}

/** Which point tag is under the playhead of a merged set video. */
export function cueIndexForPlayback(points, { localSec, clip, sourcePointId } = {}) {
  const list = Array.isArray(points) ? points : [];
  const marks = Array.isArray(clip?.point_marks) ? clip.point_marks : [];
  if (marks.length && Number.isFinite(Number(localSec))) {
    const t = Number(localSec);
    let pointId = null;
    const sorted = marks.slice().sort(
      (a, b) => (Number(a.start_sec) || 0) - (Number(b.start_sec) || 0)
    );
    for (const mark of sorted) {
      if ((Number(mark.start_sec) || 0) <= t + 0.08) pointId = mark.point_id;
      else break;
    }
    if (pointId != null) {
      const idx = list.findIndex((point) => String(point.point_id) === String(pointId));
      if (idx >= 0) return idx;
    }
  }
  if (sourcePointId != null) {
    const idx = list.findIndex(
      (point) => String(point.point_id) === String(sourcePointId)
    );
    if (idx >= 0) return idx;
  }
  return -1;
}

/** Atomic capture time for the current playhead (ms since media_sync_at). */
export function playbackAtomicMs(clip, currentTimeSec) {
  const clipT =
    clip?.t_ms != null && Number.isFinite(Number(clip.t_ms))
      ? Number(clip.t_ms)
      : 0;
  const t =
    Number.isFinite(Number(currentTimeSec)) ? Number(currentTimeSec) : 0;
  return Math.max(0, clipT + t * 1000);
}

/**
 * Ordered upload segments for one capture (pre-merge ~50MB chunks).
 * Each clip.t_ms is the offset from media_sync_at / capture start.
 */
export function captureTapeSegments(media, { captureId, rubberId } = {}) {
  const cap = captureId != null ? String(captureId) : "";
  if (!cap) return [];
  return matchPlaybackMedia(media)
    .filter((m) => {
      if (m?.kind !== "clip" || !m.file_url) return false;
      if (!m.capture_id || String(m.capture_id) !== cap) return false;
      if (
        rubberId &&
        m.rubber_id &&
        String(m.rubber_id) !== String(rubberId)
      ) {
        return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => {
      const dt = Number(a.t_ms || 0) - Number(b.t_ms || 0);
      if (dt !== 0) return dt;
      return Date.parse(a.created_at || 0) - Date.parse(b.created_at || 0);
    });
}

function tapeSpanMs(segments) {
  const rows = Array.isArray(segments) ? segments : [];
  if (!rows.length) return 0;
  const t0 = Number(rows[0]?.t_ms) || 0;
  const t1 = Number(rows[rows.length - 1]?.t_ms) || 0;
  return Math.max(0, t1 - t0);
}

/**
 * Every clip for this rubber, in capture order, with t_ms rewritten so the
 * review player can play them as one continuous tape.
 * Chunks that never received a capture_id are still included, in upload order.
 */
export function stitchRubberClipTape(media, { rubberId } = {}) {
  const clips = matchPlaybackMedia(media).filter((m) => {
    if (m?.kind !== "clip" || !m.file_url) return false;
    if (!rubberId) return true;
    if (m.rubber_id && String(m.rubber_id) !== String(rubberId)) return false;
    return Boolean(m.rubber_id);
  });
  const groups = new Map();
  const loose = [];
  for (const clip of clips) {
    if (!clip.capture_id) {
      loose.push(clip);
      continue;
    }
    const key = `c:${clip.capture_id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(clip);
  }
  if (loose.length) groups.set("loose", loose);
  const ordered = [...groups.values()].map((segs) =>
    segs.slice().sort((a, b) => {
      const dt = (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0);
      if (dt !== 0) return dt;
      return (
        (Date.parse(a.created_at || a.captured_at || 0) || 0) -
        (Date.parse(b.created_at || b.captured_at || 0) || 0)
      );
    })
  );
  ordered.sort((a, b) => {
    const ta = Date.parse(a[0].captured_at || a[0].created_at || 0) || 0;
    const tb = Date.parse(b[0].captured_at || b[0].created_at || 0) || 0;
    if (ta !== tb) return ta - tb;
    return (Number(a[0].t_ms) || 0) - (Number(b[0].t_ms) || 0);
  });

  const out = [];
  let cursor = 0;
  for (const segs of ordered) {
    const base = Number(segs[0].capture_t_ms ?? segs[0].t_ms) || 0;
    let groupEnd = 0;
    let step = 164000;
    segs.forEach((seg, index) => {
      const origin = Number(seg.capture_t_ms ?? seg.t_ms) || 0;
      const rel = Math.max(0, origin - base);
      const next = segs[index + 1];
      const nextOrigin = next ? Number(next.capture_t_ms ?? next.t_ms) || 0 : null;
      const nextRel =
        nextOrigin != null ? Math.max(rel, nextOrigin - base) : null;
      if (nextRel != null && nextRel > rel + 500) step = nextRel - rel;
      const measured = Number(seg.duration_sec);
      const lenMs =
        nextRel != null && nextRel > rel + 500
          ? nextRel - rel
          : Number.isFinite(measured) && measured > 0.5
            ? measured * 1000
            : step;
      out.push({
        ...seg,
        capture_t_ms: origin,
        t_ms: cursor + rel,
      });
      groupEnd = Math.max(groupEnd, rel + lenMs);
    });
    cursor += groupEnd;
  }
  return out;
}

function pointTms(point) {
  const n = Number(point?.t_ms);
  return Number.isFinite(n) ? n : null;
}

/** Capture chunks for this rubber only — never another match's clips. */
function looseMatchChunks(media, rubberId) {
  if (!rubberId) return [];
  return matchPlaybackMedia(media).filter((item) => {
    if (item?.kind !== "clip" || !item.file_url) return false;
    if (Array.isArray(item.point_marks) && item.point_marks.length) return false;
    if (item.point_id) return false;
    if (!item.rubber_id || String(item.rubber_id) !== String(rubberId)) return false;
    return true;
  });
}

function sortChunks(segs) {
  return (segs || []).slice().sort((a, b) => {
    const dt = (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0);
    if (dt !== 0) return dt;
    return (
      (Date.parse(a.created_at || a.captured_at || 0) || 0) -
      (Date.parse(b.created_at || b.captured_at || 0) || 0)
    );
  });
}

function clipCapturedMs(clip) {
  const t = Date.parse(clip?.captured_at || clip?.created_at || 0);
  return Number.isFinite(t) ? t : 0;
}

/**
 * One playable recording on a rubber.
 * Chunks that share a capture_id stay one tape.
 * Separate full uploads (no shared timeline) stay separate, so moving a
 * video onto a match that already has one does not hide either file.
 */
export function rubberReviewTapes(media, rubberId) {
  if (!rubberId) return [];
  const clips = matchPlaybackMedia(media).filter(
    (item) =>
      item?.kind === "clip" &&
      item.file_url &&
      item.rubber_id &&
      String(item.rubber_id) === String(rubberId)
  );
  const byCap = new Map();
  const solo = [];
  for (const clip of clips) {
    if (clip.capture_id) {
      const key = String(clip.capture_id);
      if (!byCap.has(key)) byCap.set(key, []);
      byCap.get(key).push(clip);
    } else {
      solo.push(clip);
    }
  }
  const tapes = [];
  for (const [captureId, segs] of byCap) {
    const sorted = sortChunks(segs);
    tapes.push({
      id: `c:${captureId}`,
      captureId,
      clips: sorted,
      capturedAt: clipCapturedMs(sorted[0]),
    });
  }
  if (solo.length) {
    const sorted = sortChunks(solo);
    if (sorted.length > 1 && tapeOffsetsAreUsable(sorted)) {
      tapes.push({
        id: "loose",
        captureId: "",
        clips: sorted,
        capturedAt: clipCapturedMs(sorted[0]),
      });
    } else {
      sorted.forEach((clip, index) => {
        tapes.push({
          id: `m:${clip.media_id || index}`,
          captureId: "",
          clips: [clip],
          capturedAt: clipCapturedMs(clip),
        });
      });
    }
  }
  tapes.sort(
    (a, b) => a.capturedAt - b.capturedAt || String(a.id).localeCompare(String(b.id))
  );
  return tapes;
}

/**
 * Which tape to open.
 * A full upload recorded during the points wins.
 * Otherwise a full upload just before the points beats a later leftover chunk.
 * With no points, the earliest full upload wins.
 */
export function defaultRubberTapeId(tapes, points = []) {
  const list = Array.isArray(tapes) ? tapes : [];
  if (!list.length) return "";
  if (list.length === 1) return list[0].id;
  const times = (points || [])
    .map((point) => Date.parse(point?.occurred_at || ""))
    .filter((n) => Number.isFinite(n));
  const start = times.length ? Math.min(...times) : null;
  const end = times.length ? Math.max(...times) : null;
  const ranked = list.map((tape) => {
    const clips = tape.clips || [];
    const chunked = clips.length >= 2 && tapeOffsetsAreUsable(clips);
    const fullFile = !chunked && clips.length === 1 && !tape.captureId;
    const during =
      start != null &&
      tape.capturedAt &&
      tape.capturedAt >= start - 3 * 60 * 1000 &&
      tape.capturedAt <= end + 60 * 1000;
    const before =
      start != null &&
      tape.capturedAt &&
      tape.capturedAt <= start + 60 * 1000 &&
      !during;
    const near =
      start != null && before
        ? Math.max(0, 30 * 60 * 1000 - (start - tape.capturedAt))
        : 0;
    return {
      tape,
      score:
        (fullFile && during ? 8000 : 0) +
        (fullFile && before ? 5000 : 0) +
        (fullFile && start == null ? 1000 : 0) +
        (chunked ? clips.length * 100 : 0) +
        near / 1000,
    };
  });
  ranked.sort(
    (a, b) =>
      b.score - a.score || a.tape.capturedAt - b.tape.capturedAt
  );
  return ranked[0].tape.id;
}

function groupChunksByCapture(chunks) {
  const groups = new Map();
  for (const clip of chunks || []) {
    const key = clip.capture_id ? `c:${clip.capture_id}` : "loose";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(clip);
  }
  const ordered = [...groups.entries()].map(([key, segs]) => ({
    key,
    captureId: key.startsWith("c:") ? key.slice(2) : "",
    segs: sortChunks(segs),
  }));
  ordered.sort((a, b) => {
    const ta = Date.parse(a.segs[0].captured_at || a.segs[0].created_at || 0) || 0;
    const tb = Date.parse(b.segs[0].captured_at || b.segs[0].created_at || 0) || 0;
    if (ta !== tb) return ta - tb;
    return (Number(a.segs[0].t_ms) || 0) - (Number(b.segs[0].t_ms) || 0);
  });
  return ordered;
}

function chunkEndMs(seg, next) {
  const start = Number(seg?.t_ms) || 0;
  if (next) {
    const gap = (Number(next.t_ms) || 0) - start;
    if (gap > 500) return start + gap;
  }
  const measured = Number(seg?.duration_sec);
  if (Number.isFinite(measured) && measured > 0.5) return start + measured * 1000;
  return start + 60000;
}

function boundsForSet(points, setNumber) {
  const setNum = Number(setNumber);
  const stamped = (points || []).filter((point) => pointTms(point) != null);
  const mine = stamped.filter((point) => (Number(point.set_number) || 1) === setNum);
  if (!mine.length) return null;
  const later = stamped.filter((point) => (Number(point.set_number) || 1) > setNum);
  const earlier = stamped.filter((point) => (Number(point.set_number) || 1) < setNum);
  return {
    start: earlier.length ? Math.min(...mine.map(pointTms)) : 0,
    end: later.length ? Math.min(...later.map(pointTms)) : Infinity,
  };
}

function joinCaptureGroups(groups, rubberId) {
  if (!groups.length) return [];
  if (groups.length === 1) return groups[0].segs;
  return stitchRubberClipTape(
    groups.flatMap((group) => group.segs),
    { rubberId }
  );
}

/**
 * Join 15s capture chunks into one review tape.
 * Point tags → one tape per set, still on that capture's clock.
 * No point tags → one tape for the whole rubber.
 * Chunks from any other rubber are ignored.
 */
export function stitchMatchRecordingChunks(
  chunks,
  { rubberId, setNumber, points = [] } = {}
) {
  const list = looseMatchChunks(chunks, rubberId);
  if (list.length < 2) return [];
  const roster = points || [];
  if (!roster.length) {
    return joinCaptureGroups(groupChunksByCapture(list), rubberId);
  }
  const setNum = Number(setNumber);
  const setPoints = roster.filter(
    (point) => (Number(point.set_number) || 1) === setNum
  );
  if (!setPoints.length) return [];
  const caps = new Set(
    setPoints
      .map((point) => (point.capture_id != null ? String(point.capture_id) : ""))
      .filter(Boolean)
  );
  const groups = groupChunksByCapture(list).filter((group) => {
    if (!caps.size) return true;
    return group.captureId && caps.has(group.captureId);
  });
  const picked = [];
  for (const group of groups) {
    const capPoints = roster.filter((point) => {
      if (!group.captureId) return !point.capture_id;
      if (!point.capture_id) return caps.size === 1;
      return String(point.capture_id) === group.captureId;
    });
    const mine = setPoints.filter((point) => {
      if (!group.captureId) return !point.capture_id;
      if (!point.capture_id) return caps.size === 1;
      return String(point.capture_id) === group.captureId;
    });
    const bounds = boundsForSet(capPoints, setNum);
    group.segs.forEach((seg, index) => {
      if (seg.set_number != null && Number(seg.set_number) !== setNum) return;
      if (seg.set_number != null && Number(seg.set_number) === setNum) {
        picked.push(seg);
        return;
      }
      const start = Number(seg.t_ms) || 0;
      const end = chunkEndMs(seg, group.segs[index + 1]);
      const inWindow = bounds ? start >= bounds.start && start < bounds.end : false;
      const holdsPoint = mine.some((point) => {
        const t = pointTms(point);
        return t != null && t >= start && t < end;
      });
      if (inWindow || holdsPoint) picked.push(seg);
    });
  }
  return joinCaptureGroups(groupChunksByCapture(picked), rubberId);
}

/** Seek a tagged point on a stitched chunk tape without leaving its capture clock. */
export function locatePointOnTape(sources, point) {
  const rows = Array.isArray(sources) ? sources : [];
  if (!rows.length || point?.t_ms == null || !Number.isFinite(Number(point.t_ms))) {
    return null;
  }
  const t = Number(point.t_ms);
  const cap = point.capture_id != null ? String(point.capture_id) : "";
  let idx = -1;
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (cap && row?.capture_id && String(row.capture_id) !== cap) continue;
    const origin = Number(row?.capture_t_ms != null ? row.capture_t_ms : row?.t_ms) || 0;
    if (origin <= t + 40) idx = i;
    else if (!cap) break;
  }
  if (idx < 0) return null;
  const row = rows[idx];
  const origin = Number(row?.capture_t_ms != null ? row.capture_t_ms : row?.t_ms) || 0;
  return {
    index: idx,
    localSec: Math.max(0, (t - origin) / 1000),
  };
}

/**
 * Pick the multi-chunk tape for a rubber/set.
 * Prefer capture_ids stamped on the set's points (and the clip for that set);
 * only then fall back to the longest capture still on the rubber.
 */
export function resolveCaptureTapeSegments(
  media,
  { rubberId, setPoints = [], clip = null } = {}
) {
  const rank = (list) => {
    const scored = list.slice();
    scored.sort((a, b) => {
      if (b.length !== a.length) return b.length - a.length;
      return tapeSpanMs(b) - tapeSpanMs(a);
    });
    return scored[0] || [];
  };

  const fromPoints = [];
  const seen = new Set();
  const pushInto = (bucket, captureId) => {
    const cap = captureId != null ? String(captureId) : "";
    if (!cap || seen.has(`p:${cap}`)) return;
    seen.add(`p:${cap}`);
    const segs = captureTapeSegments(media, { captureId: cap, rubberId });
    if (segs.length) bucket.push(segs);
  };

  for (const p of setPoints || []) pushInto(fromPoints, p?.capture_id);
  pushInto(fromPoints, clip?.capture_id);
  if (fromPoints.length) return rank(fromPoints);

  const fromRubber = [];
  for (const m of matchPlaybackMedia(media)) {
    if (m?.kind !== "clip" || !m.file_url || !m.capture_id) continue;
    if (
      rubberId &&
      m.rubber_id &&
      String(m.rubber_id) !== String(rubberId)
    ) {
      continue;
    }
    const cap = String(m.capture_id);
    if (seen.has(`r:${cap}`)) continue;
    seen.add(`r:${cap}`);
    const segs = captureTapeSegments(media, { captureId: cap, rubberId });
    if (segs.length) fromRubber.push(segs);
  }
  return rank(fromRubber);
}

/**
 * True when segment t_ms values form a usable increasing timeline.
 */
export function tapeOffsetsAreUsable(segments) {
  const rows = Array.isArray(segments) ? segments : [];
  if (rows.length <= 1) return true;
  const span = tapeSpanMs(rows);
  // 15s match chunks are a real timeline. Flat or sub-second t_ms are not.
  return span >= Math.max(10_000, (rows.length - 1) * 10_000);
}

/**
 * Build per-segment start offsets (seconds). Uses t_ms when usable;
 * otherwise stacks measured/estimated chunk lengths end-to-end.
 */
export function tapeSegmentStartsSec(segments, measuredDurations = null) {
  const rows = Array.isArray(segments) ? segments : [];
  if (!rows.length) return [];
  if (tapeOffsetsAreUsable(rows)) {
    return rows.map((r) => Math.max(0, Number(r.t_ms) || 0) / 1000);
  }
  const starts = [];
  let cursor = 0;
  let fallback = 164;
  for (let i = 0; i < rows.length; i += 1) {
    starts.push(cursor);
    const measured =
      measuredDurations && Number.isFinite(Number(measuredDurations[i]))
        ? Number(measuredDurations[i])
        : Number(rows[i]?.duration_sec);
    const len =
      Number.isFinite(measured) && measured > 0.5 ? measured : fallback;
    if (Number.isFinite(measured) && measured > 0.5) fallback = measured;
    cursor += len;
  }
  return starts;
}

/**
 * Best-effort total tape length in seconds from segment offsets.
 * Mid segments span until the next chunk's t_ms; the last uses measured
 * duration_sec when known, else the previous chunk length (≈2:44).
 * `measuredDurations` may be a number (last seg) or an array/map by index.
 */
export function estimateTapeDurationSec(segments, measuredDurations = null) {
  const rows = Array.isArray(segments) ? segments : [];
  if (!rows.length) return 0;

  let lastOnly = null;
  let byIndex = null;
  if (Array.isArray(measuredDurations)) {
    byIndex = measuredDurations;
  } else if (
    measuredDurations &&
    typeof measuredDurations === "object"
  ) {
    byIndex = measuredDurations;
  } else if (
    measuredDurations != null &&
    Number.isFinite(Number(measuredDurations))
  ) {
    lastOnly = Number(measuredDurations);
  }

  const starts = tapeSegmentStartsSec(rows, byIndex);
  const lastStart = starts[starts.length - 1] || 0;
  let prevLen = 164;
  if (starts.length > 1) {
    prevLen = Math.max(0.5, starts[starts.length - 1] - starts[starts.length - 2]);
  }
  const measuredLast =
    lastOnly != null
      ? lastOnly
      : byIndex && Number.isFinite(Number(byIndex[rows.length - 1]))
        ? Number(byIndex[rows.length - 1])
        : Number(rows[rows.length - 1]?.duration_sec);
  const lastLen =
    Number.isFinite(measuredLast) && measuredLast > 0.5
      ? measuredLast
      : prevLen;

  if (byIndex) {
    let sum = 0;
    let known = 0;
    for (let i = 0; i < rows.length; i += 1) {
      const d = Number(byIndex[i]);
      if (Number.isFinite(d) && d > 0.5) {
        sum += d;
        known += 1;
      }
    }
    if (known === rows.length) return sum;
    if (known > 0) {
      const avg = sum / known;
      return sum + avg * (rows.length - known);
    }
  }

  return Math.max(prevLen, lastStart + lastLen);
}

/**
 * Map a global capture time (seconds from media_sync_at) onto a segment.
 */
export function locateTapeSegment(segments, globalSec, measuredDurations = null) {
  const rows = Array.isArray(segments) ? segments : [];
  if (!rows.length) return null;
  const t = Math.max(0, Number(globalSec) || 0);
  const starts = tapeSegmentStartsSec(rows, measuredDurations);
  let idx = 0;
  for (let i = 0; i < starts.length; i += 1) {
    if (starts[i] <= t + 0.001) idx = i;
    else break;
  }
  const startSec = starts[idx] || 0;
  return {
    index: idx,
    segment: rows[idx],
    localSec: Math.max(0, t - startSec),
    startSec,
  };
}
/**
 * Active cue = last point whose atomic t_ms is at or before the playhead.
 * Returns -1 when the playhead is before the first stamped point.
 */
export function activeCueIndex(points, atomicMs) {
  if (!Array.isArray(points) || !points.length) return -1;
  if (!Number.isFinite(Number(atomicMs))) return -1;
  const t = Number(atomicMs);
  let best = -1;
  for (let i = 0; i < points.length; i += 1) {
    const pt = points[i]?.t_ms;
    if (pt == null || !Number.isFinite(Number(pt))) continue;
    if (Number(pt) <= t + 40) best = i;
    else break;
  }
  return best;
}

function pointPair(point) {
  if (!point) return null;
  if (point.home_score == null || point.home_score === "") return null;
  if (point.away_score == null || point.away_score === "") return null;
  const home = Number(point.home_score);
  const away = Number(point.away_score);
  if (!Number.isFinite(home) || !Number.isFinite(away)) return null;
  return { home, away };
}

function tableTennisSetOver(home, away) {
  return Math.max(home, away) >= 11 && Math.abs(home - away) >= 2;
}

function clipSetCount(value) {
  if (value == null || value === "") return null;
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(7, n));
}

/**
 * Live scorecam set tracking. Umpire "games" digits often still read 0–0 on a
 * finished 11–x (and on the following 0–0), so award the set locally when the
 * point score closes or when 0–0 opens the next set.
 */
export function planLiveScoreStamp(mapped, prev, tracked, labels = {}) {
  const ocrHome = clipSetCount(mapped?.gamesHome);
  const ocrAway = clipSetCount(mapped?.gamesAway);
  let setsHome = Math.max(0, Number(tracked?.home) || 0);
  let setsAway = Math.max(0, Number(tracked?.away) || 0);
  let stampSet = Math.max(1, Math.min(9, Number(tracked?.setNumber) || 1));
  let openNext = false;
  let setNote = "";
  const finished = tableTennisSetOver(mapped?.home, mapped?.away);
  const restart =
    Number(mapped?.home) === 0 &&
    Number(mapped?.away) === 0 &&
    prev &&
    tableTennisSetOver(prev.home, prev.away);

  // Mid-set: trust OCR games only when they have caught up. A lagging 0–0
  // read must not wipe sets already awarded after 11–x.
  if (ocrHome != null && ocrAway != null && !finished && !restart) {
    const ocrSum = ocrHome + ocrAway;
    const trackedSum = setsHome + setsAway;
    if (ocrSum >= trackedSum) {
      setsHome = ocrHome;
      setsAway = ocrAway;
    }
  } else if (
    ocrHome != null &&
    ocrAway != null &&
    ocrHome + ocrAway >= stampSet
  ) {
    setsHome = ocrHome;
    setsAway = ocrAway;
  }

  const awardSetWin = (homePts, awayPts) => {
    if (setsHome + setsAway !== stampSet - 1) return;
    if (homePts > awayPts) setsHome += 1;
    else setsAway += 1;
  };

  if (finished) {
    awardSetWin(mapped.home, mapped.away);
    setNote =
      mapped.home > mapped.away
        ? ` · set to ${labels.home || "home"}`
        : ` · set to ${labels.away || "away"}`;
  } else if (restart) {
    awardSetWin(prev.home, prev.away);
    openNext = true;
    stampSet = Math.max(1, Math.min(9, stampSet + 1));
    setNote = ` · set ${setsHome}–${setsAway} confirmed`;
  }

  const setsText = `${setsHome}–${setsAway}`;
  return {
    setsHome,
    setsAway,
    stampSet,
    openNext,
    setsText,
    scoreKey: `${stampSet}|${setsText}|${mapped.home}-${mapped.away}`,
    scoreLabel: `${mapped.home}–${mapped.away} · sets ${setsText}`,
    setNote,
    finished,
    restart,
  };
}

/**
 * Scoreboard at a playhead: the point on screen, and games already won
 * before that point's set.
 */
export function broadcastScoreAtPlayhead(points, playheadMs) {
  const list = (Array.isArray(points) ? points : []).slice().sort((a, b) => {
    const ta = a?.t_ms == null ? Number.POSITIVE_INFINITY : Number(a.t_ms);
    const tb = b?.t_ms == null ? Number.POSITIVE_INFINITY : Number(b.t_ms);
    if (ta !== tb) return ta - tb;
    return (Number(a?.seq) || 0) - (Number(b?.seq) || 0);
  });
  const idx = activeCueIndex(list, playheadMs);
  const point = idx >= 0 ? list[idx] : null;
  const pair = pointPair(point);
  const setNumber = point ? Math.max(1, Number(point.set_number) || 1) : 1;
  const lastBySet = new Map();
  for (const row of list) {
    const n = Math.max(1, Number(row?.set_number) || 1);
    if (n >= setNumber) continue;
    if (!pointPair(row)) continue;
    lastBySet.set(n, row);
  }
  let gamesHome = 0;
  let gamesAway = 0;
  const stampedHome = Number(point?.sets_home);
  const stampedAway = Number(point?.sets_away);
  if (Number.isFinite(stampedHome) && Number.isFinite(stampedAway)) {
    gamesHome = Math.max(0, Math.floor(stampedHome));
    gamesAway = Math.max(0, Math.floor(stampedAway));
  } else {
    for (const row of lastBySet.values()) {
      const score = pointPair(row);
      if (!score || !tableTennisSetOver(score.home, score.away)) continue;
      if (score.home > score.away) gamesHome += 1;
      else gamesAway += 1;
    }
  }
  return {
    home: pair ? pair.home : null,
    away: pair ? pair.away : null,
    setNumber,
    gamesHome,
    gamesAway,
    pointId: point?.point_id || null,
  };
}

/**
 * Team score at a playhead: rubbers already decided by that moment.
 * The sheet's final 6–4 is not used. A rubber counts only when one side
 * has reached setsToWin on points stamped at or before the playhead.
 */
export function teamMatchesAtPlayhead(points, playheadMs, setsToWin = 3) {
  const need = Math.max(2, Number(setsToWin) || 3);
  const t = Number(playheadMs);
  if (!Number.isFinite(t)) return { home: 0, away: 0 };
  const byRubber = new Map();
  for (const point of Array.isArray(points) ? points : []) {
    if (point?.rubber_id == null) continue;
    const pt = point?.t_ms;
    if (pt == null || !Number.isFinite(Number(pt)) || Number(pt) > t + 40) continue;
    if (!pointPair(point)) continue;
    const key = String(point.rubber_id);
    if (!byRubber.has(key)) byRubber.set(key, []);
    byRubber.get(key).push(point);
  }
  let home = 0;
  let away = 0;
  for (const rows of byRubber.values()) {
    const sets = setsWonFromTimedPoints(rows);
    if (sets.home >= need && sets.home > sets.away) home += 1;
    else if (sets.away >= need && sets.away > sets.home) away += 1;
  }
  return { home, away };
}

/** Official rubber winner from the sheet (parallel tables / finished matches). */
export function rubberSheetWinner(rubber) {
  if (rubber?.winner_side === "home" || rubber?.winner_side === "away") {
    return rubber.winner_side;
  }
  if (rubber?.state && rubber.state !== "done") return null;
  const score = rubber?.set_scores || {};
  const hs = Number(score.home_sets) || 0;
  const as = Number(score.away_sets) || 0;
  if (hs > as && hs + as > 0) return "home";
  if (as > hs) return "away";
  return null;
}

export function teamTalliesFromRubbers(rubbers) {
  let home = 0;
  let away = 0;
  for (const rubber of Array.isArray(rubbers) ? rubbers : []) {
    const side = rubberSheetWinner(rubber);
    if (side === "home") home += 1;
    else if (side === "away") away += 1;
  }
  return { home, away };
}

/**
 * Broadcast plate team score for the rubber on screen: count sheet wins from
 * other finished matches (parallel tables / earlier roster rows), then add the
 * active rubber only when its timed points already decide it. Never use the
 * final feuille total (e.g. 10–0) while reviewing one match mid-rencontre.
 */
export function teamBoardScoreAtPlayhead({
  points,
  playheadMs,
  setsToWin = 3,
  rubbers = null,
  activeRubberId = null,
  sheetHome = null,
  sheetAway = null,
} = {}) {
  const list = Array.isArray(rubbers) ? rubbers : [];
  const activeKey =
    activeRubberId != null && String(activeRubberId) !== ""
      ? String(activeRubberId)
      : null;

  if (list.length) {
    let home = 0;
    let away = 0;
    for (const row of list) {
      const key = String(row?.rubber_id ?? "");
      if (activeKey && key === activeKey) continue;
      const side = rubberSheetWinner(row);
      if (side === "home") home += 1;
      else if (side === "away") away += 1;
    }
    if (activeKey) {
      const activePoints = (Array.isArray(points) ? points : []).filter(
        (p) => String(p?.rubber_id) === activeKey
      );
      const timed = teamMatchesAtPlayhead(activePoints, playheadMs, setsToWin);
      home += timed.home;
      away += timed.away;
    } else {
      const timed = teamMatchesAtPlayhead(points, playheadMs, setsToWin);
      if (timed.home + timed.away > home + away) return timed;
    }
    return { home, away };
  }

  const playhead = teamMatchesAtPlayhead(points, playheadMs, setsToWin);
  if (playhead.home + playhead.away > 0) return playhead;

  const sh = Number(sheetHome);
  const sa = Number(sheetAway);
  if (Number.isFinite(sh) && Number.isFinite(sa)) {
    return {
      home: Math.max(0, Math.floor(sh)),
      away: Math.max(0, Math.floor(sa)),
    };
  }
  return { home: 0, away: 0 };
}

function setsWonFromTimedPoints(rows) {
  const sorted = rows.slice().sort((a, b) => {
    const ta = Number(a?.t_ms) || 0;
    const tb = Number(b?.t_ms) || 0;
    if (ta !== tb) return ta - tb;
    return (Number(a?.seq) || 0) - (Number(b?.seq) || 0);
  });
  const lastBySet = new Map();
  for (const row of sorted) {
    lastBySet.set(Math.max(1, Number(row?.set_number) || 1), row);
  }
  let home = 0;
  let away = 0;
  for (const row of lastBySet.values()) {
    const score = pointPair(row);
    if (!score || !tableTennisSetOver(score.home, score.away)) continue;
    if (score.home > score.away) home += 1;
    else away += 1;
  }
  return { home, away };
}

/**
 * Wall-clock offset from the shared media_sync_at epoch.
 * Prefer this over performance.now() so camera phones stay on the same axis
 * as scorer point stamps.
 */
export function atomicOffsetFromSync(mediaSyncAt, nowMs = Date.now()) {
  const sync = Number(mediaSyncAt);
  const now = Number(nowMs);
  if (!Number.isFinite(sync) || sync <= 0 || !Number.isFinite(now)) {
    return 0;
  }
  return Math.max(0, Math.round(now - sync));
}

export function groupPointsBySet(points) {
  const groups = [];
  const index = new Map();
  for (const point of points || []) {
    const n = Math.max(1, Number(point.set_number) || 1);
    if (!index.has(n)) {
      const group = { setNumber: n, points: [] };
      index.set(n, group);
      groups.push(group);
    }
    index.get(n).points.push(point);
  }
  return groups;
}

export function clipForPoint(media, point, allPoints = []) {
  if (!point) return null;
  const clips = matchPlaybackMedia(media).filter((m) => m.kind === "clip");
  const byId = clips.find(
    (m) => m.point_id && String(m.point_id) === String(point.point_id)
  );
  if (byId) return byId;
  if (point.capture_id) {
    const byCap = clips
      .filter((m) => {
        if (!m.capture_id || String(m.capture_id) !== String(point.capture_id)) {
          return false;
        }
        // Prefer clips on the same rubber so a later score edit cannot pull
        // another rubber's tape into this review.
        if (
          point.rubber_id &&
          m.rubber_id &&
          String(m.rubber_id) !== String(point.rubber_id)
        ) {
          return false;
        }
        return true;
      })
      .slice()
      .sort((a, b) => Number(a.t_ms || 0) - Number(b.t_ms || 0));
    if (byCap.length === 1) return byCap[0];
    if (byCap.length > 1) {
      // Pre-merge segments: each clip.t_ms is the offset from capture start.
      if (point.t_ms != null && Number.isFinite(Number(point.t_ms))) {
        const t = Number(point.t_ms);
        let chosen = byCap[0];
        for (const clip of byCap) {
          if (Number(clip.t_ms || 0) <= t) chosen = clip;
          else break;
        }
        return chosen;
      }
      return byCap[0];
    }
  }
  const mates = (allPoints || []).filter(
    (p) =>
      String(p.rubber_id) === String(point.rubber_id) &&
      Number(p.set_number) === Number(point.set_number)
  );
  const times = mates
    .map((p) => Date.parse(p.occurred_at))
    .filter((n) => Number.isFinite(n));
  const t0 = times.length ? Math.min(...times) : Date.parse(point.occurred_at);
  const t1 = times.length ? Math.max(...times) : t0;
  const rubberClips = clips.filter(
    (m) =>
      !point.rubber_id ||
      !m.rubber_id ||
      String(m.rubber_id) === String(point.rubber_id)
  );
  return (
    rubberClips.find((m) => {
      const captured = Date.parse(m.captured_at);
      if (!Number.isFinite(captured) || !Number.isFinite(t0)) return false;
      return captured >= t0 - 60000 && captured <= t1 + 180000;
    }) ||
    rubberClips[rubberClips.length - 1] ||
    null
  );
}

export function mediaForPoint(media, point, windowMs = 45000) {
  if (!point) return [];
  const t = Date.parse(point.occurred_at);
  const chosenClip =
    point.capture_id ? clipForPoint(media, point) : null;
  return (media || []).filter((m) => {
    if (isScorecamMedia(m) && m.kind === "clip") return false;
    if (m.point_id && String(m.point_id) === String(point.point_id)) return true;
    if (
      m.kind === "clip" &&
      point.capture_id &&
      m.capture_id &&
      String(m.capture_id) === String(point.capture_id)
    ) {
      if (
        point.rubber_id &&
        m.rubber_id &&
        String(m.rubber_id) !== String(point.rubber_id)
      ) {
        return false;
      }
      return (
        chosenClip && String(chosenClip.media_id) === String(m.media_id)
      );
    }
    if (
      m.kind === "voice" &&
      point.capture_id &&
      m.capture_id &&
      String(m.capture_id) === String(point.capture_id)
    ) {
      if (
        point.rubber_id &&
        m.rubber_id &&
        String(m.rubber_id) !== String(point.rubber_id)
      ) {
        return false;
      }
      return true;
    }
    if (point.t_ms != null && m.t_ms != null && m.kind !== "clip") {
      return Math.abs(Number(m.t_ms) - Number(point.t_ms)) <= windowMs;
    }
    if (Number.isFinite(t) && m.captured_at && m.kind !== "clip") {
      return Math.abs(Date.parse(m.captured_at) - t) <= windowMs;
    }
    return false;
  });
}

/** Clips/photos/voice for a rubber that are not already attached to a listed point. */
export function orphanMediaForRubber(media, rubberId, points = []) {
  const attached = new Set();
  const tapeCaps = new Set();
  for (const point of points || []) {
    for (const m of mediaForPoint(media, point)) {
      if (m?.media_id) attached.add(String(m.media_id));
    }
    const clip = clipForPoint(media, point, points);
    if (clip?.media_id) attached.add(String(clip.media_id));
    if (point?.capture_id) tapeCaps.add(String(point.capture_id));
    if (clip?.capture_id) tapeCaps.add(String(clip.capture_id));
  }
  // Other ~50MB chunks of an active capture belong to the tape player, not
  // a separate "other media" gallery of 2:44 singles.
  for (const cap of tapeCaps) {
    for (const seg of captureTapeSegments(media, { captureId: cap, rubberId })) {
      if (seg?.media_id) attached.add(String(seg.media_id));
    }
  }
  return matchPlaybackMedia(media).filter((m) => {
    if (!m?.media_id || attached.has(String(m.media_id))) return false;
    if (!rubberId) return true;
    return m.rubber_id && String(m.rubber_id) === String(rubberId);
  });
}

export function swapSeats(slots, seatA, seatB) {
  const copy = (slots || []).map((s) => ({ ...s }));
  const a = copy.find((s) => Number(s.seat) === Number(seatA));
  const b = copy.find((s) => Number(s.seat) === Number(seatB));
  if (!a || !b) return copy;
  const keys = [
    "user_id",
    "player_id",
    "display_name",
    "classement",
    "ranking_points",
    "average_points",
  ];
  for (const key of keys) {
    const tmp = a[key];
    a[key] = b[key];
    b[key] = tmp;
  }
  a.order_source = "same_level_override";
  b.order_source = "same_level_override";
  return copy.sort((x, y) => Number(x.seat) - Number(y.seat));
}

export function seatsInSameTie(tieGroups, seatA, seatB) {
  return (tieGroups || []).some(
    (group) => group.includes(Number(seatA)) && group.includes(Number(seatB))
  );
}

function eventTimeMs(pointOrMedia, originAbs = null) {
  if (pointOrMedia?.t_ms != null && Number.isFinite(Number(pointOrMedia.t_ms))) {
    return Number(pointOrMedia.t_ms);
  }
  const iso = pointOrMedia?.occurred_at || pointOrMedia?.captured_at;
  const abs = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(abs)) return Number.MAX_SAFE_INTEGER / 4;
  if (originAbs != null && Number.isFinite(originAbs)) {
    return Math.max(0, abs - originAbs);
  }
  return abs;
}

const REVIEW_KIND_RANK = { point: 0, clip: 1, photo: 2, voice: 3 };

/**
 * Chronological review playlist for one rubber: points, then clips / photos /
 * voice / comments interleaved by capture time.
 */
export function buildRubberReviewPlaylist(points = [], media = [], rubberId) {
  const rubberPoints = (points || [])
    .filter((p) => !rubberId || String(p.rubber_id) === String(rubberId))
    .slice()
    .sort((a, b) => {
      const ta = eventTimeMs(a);
      const tb = eventTimeMs(b);
      if (ta !== tb) return ta - tb;
      return (Number(a.seq) || 0) - (Number(b.seq) || 0);
    });

  const first = rubberPoints[0];
  const originAbs =
    first?.occurred_at && first.t_ms != null
      ? Date.parse(first.occurred_at) - Number(first.t_ms)
      : first?.occurred_at
        ? Date.parse(first.occurred_at)
        : null;

  const usedMedia = new Set();
  const items = [];

  for (const point of rubberPoints) {
    const pointAt = eventTimeMs(point, originAbs);
    items.push({
      id: `point:${point.point_id}`,
      kind: "point",
      at: pointAt,
      point,
    });

    const clip = clipForPoint(media, point, rubberPoints);
    if (clip?.media_id && !usedMedia.has(String(clip.media_id))) {
      usedMedia.add(String(clip.media_id));
      const clipAt = eventTimeMs(clip, originAbs);
      items.push({
        id: `clip:${clip.media_id}`,
        kind: "clip",
        at: Number.isFinite(clipAt) ? clipAt : pointAt,
        media: clip,
        point,
      });
    }

    const assets = mediaForPoint(media, point).filter(
      (m) => m.kind === "photo" || m.kind === "voice"
    );
    for (const m of assets) {
      if (!m?.media_id || usedMedia.has(String(m.media_id))) continue;
      usedMedia.add(String(m.media_id));
      const mediaAt = eventTimeMs(m, originAbs);
      items.push({
        id: `${m.kind}:${m.media_id}`,
        kind: m.kind,
        at: Number.isFinite(mediaAt) ? mediaAt : pointAt + 1,
        media: m,
        point,
      });
    }
  }

  for (const m of orphanMediaForRubber(media, rubberId, rubberPoints)) {
    if (!m?.media_id || usedMedia.has(String(m.media_id))) continue;
    usedMedia.add(String(m.media_id));
    items.push({
      id: `${m.kind || "media"}:${m.media_id}`,
      kind:
        m.kind === "clip" || m.kind === "photo" || m.kind === "voice"
          ? m.kind
          : "photo",
      at: eventTimeMs(m, originAbs),
      media: m,
      point: null,
    });
  }

  items.sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    return (REVIEW_KIND_RANK[a.kind] ?? 9) - (REVIEW_KIND_RANK[b.kind] ?? 9);
  });

  return items;
}

function rubberScopedMedia(rubber, points = [], media = []) {
  const rubberId = rubber?.rubber_id;
  const rubberPoints = (points || []).filter(
    (p) => String(p.rubber_id) === String(rubberId)
  );
  return matchPlaybackMedia(media).filter((m) => {
    if (m.rubber_id && String(m.rubber_id) === String(rubberId)) return true;
    if (!m.rubber_id && rubberPoints.some((p) => mediaForPoint([m], p).length)) {
      return true;
    }
    return false;
  });
}

function rubberScopedClips(rubber, points = [], media = []) {
  return rubberScopedMedia(rubber, points, media).filter((m) => m.kind === "clip");
}

/**
 * Video kept on a rubber that is not live and not finalized (for example
 * clips restored after a reset). Review can open without a made-up score.
 */
export function rubberHasStoredClips(
  rubber,
  points = [],
  media = [],
  { recording = false, tableRecording = null } = {}
) {
  if (!rubber || rubber.state === "done" || rubber.state === "live") return false;
  if (recording) return false;
  const table = Number(rubber.table_number);
  if (tableRecording && Number(tableRecording.table_number) === table) {
    return false;
  }
  return rubberScopedClips(rubber, points, media).some((item) => item.file_url);
}

/** True when a finalized rubber can open review (uploads finished). */
export function rubberReviewReady(
  rubber,
  points = [],
  media = [],
  { videoFeatureOn = true, recording = false, tableRecording = null } = {}
) {
  if (!rubber || rubber.state !== "done") return false;
  const table = Number(rubber.table_number);
  if (recording) return false;
  if (tableRecording && Number(tableRecording.table_number) === table) {
    return false;
  }

  const rubberPoints = (points || []).filter(
    (p) => String(p.rubber_id) === String(rubber.rubber_id)
  );
  const rubberMedia = rubberScopedMedia(rubber, points, media);
  const rubberClips = rubberMedia.filter((m) => m.kind === "clip");

  if (!rubberPoints.length && !rubberMedia.length) return false;

  if (videoFeatureOn) {
    // Uploaded clips on this rubber unlock review even if later score edits
    // stamped points with a different / missing capture_id.
    if (rubberClips.length) return true;

    const captureIds = [
      ...new Set(rubberPoints.map((p) => p.capture_id).filter(Boolean).map(String)),
    ];
    // Timed points with no recording are a score sheet, not a match review.
    if (!captureIds.length) return false;
    for (const id of captureIds) {
      const hasClip = matchPlaybackMedia(media).some(
        (m) => m.kind === "clip" && m.capture_id && String(m.capture_id) === id
      );
      if (!hasClip) return false;
    }
  }

  return true;
}

/** True when a finalized rubber is still waiting on set videos. */
export function rubberReviewUploading(
  rubber,
  points = [],
  media = [],
  { videoFeatureOn = true, recording = false, tableRecording = null } = {}
) {
  if (!rubber || rubber.state !== "done") return false;
  if (rubberReviewReady(rubber, points, media, { videoFeatureOn, recording, tableRecording })) {
    return false;
  }
  const table = Number(rubber.table_number);
  if (recording) return true;
  if (tableRecording && Number(tableRecording.table_number) === table) {
    return true;
  }
  if (!videoFeatureOn) return false;
  // Already have rubber clips → ready path above; do not keep waiting.
  if (rubberScopedClips(rubber, points, media).length) return false;
  const rubberPoints = (points || []).filter(
    (p) => String(p.rubber_id) === String(rubber.rubber_id)
  );
  const captureIds = [
    ...new Set(rubberPoints.map((p) => p.capture_id).filter(Boolean).map(String)),
  ];
  if (!captureIds.length) return false;
  return captureIds.some(
    (id) =>
      !matchPlaybackMedia(media).some(
        (m) => m.kind === "clip" && m.capture_id && String(m.capture_id) === id
      )
  );
}

function rankingPointsOf(player) {
  const n = Number(player?.ranking_points ?? player?.fltt_ranking_points);
  return Number.isFinite(n) ? n : null;
}

function opponentPreviewName(player) {
  return String(player?.display_name || player?.name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Token-sorted so FLTT "SURNAME First" matches roster "First SURNAME". */
function canonicalOpponentPreviewName(player) {
  const normalized = opponentPreviewName(player);
  if (!normalized) return "";
  return normalized.split(/\s+/).filter(Boolean).sort().join(" ");
}

export function opponentPreviewPlayerKey(player, index = 0) {
  if (player?.user_id) return `u:${player.user_id}`;
  if (player?.player_id) return `p:${player.player_id}`;
  const name = canonicalOpponentPreviewName(player);
  if (name) return `n:${name}`;
  return `idx:${index}`;
}

export function sortPlayersByRankingPoints(players) {
  return [...(players || [])].sort((a, b) => {
    const va = rankingPointsOf(a);
    const vb = rankingPointsOf(b);
    if (va == null && vb == null) {
      return opponentPreviewName(a).localeCompare(opponentPreviewName(b));
    }
    if (va == null) return 1;
    if (vb == null) return -1;
    if (vb !== va) return vb - va;
    return opponentPreviewName(a).localeCompare(opponentPreviewName(b));
  });
}

export function sameOpponentPlayerSet(a, b) {
  const left = (a || [])
    .map((p, i) => opponentPreviewPlayerKey(p, i))
    .filter((k) => k && !k.startsWith("idx:"));
  const right = (b || [])
    .map((p, i) => opponentPreviewPlayerKey(p, i))
    .filter((k) => k && !k.startsWith("idx:"));
  if (left.length !== 4 || right.length !== 4) return false;
  const sb = new Set(right);
  return left.every((k) => sb.has(k));
}

function opponentPreviewKeySet(players) {
  return new Set(
    (players || [])
      .map((p, i) => opponentPreviewPlayerKey(p, i))
      .filter((k) => k && !k.startsWith("idx:"))
  );
}

function excludePreviewPlayers(candidates, taken) {
  const keys = opponentPreviewKeySet(taken);
  return (candidates || []).filter((p, i) => {
    const key = opponentPreviewPlayerKey(p, i);
    return key && !key.startsWith("idx:") && !keys.has(key);
  });
}

function padTitularFromRanking(titular, ranked, size) {
  const next = [...(titular || [])];
  for (const player of ranked || []) {
    if (next.length >= size) break;
    const key = opponentPreviewPlayerKey(player);
    if (!key || key.startsWith("idx:")) continue;
    if (opponentPreviewKeySet(next).has(key)) continue;
    next.push(player);
  }
  return next.slice(0, size);
}

/**
 * Opponent four for the next championship match.
 * Last-feuille players start: they are likelier to play again. Ranking-band
 * names who were not on that sheet fill empty seats and extras (possible new players).
 */
export function resolveOpponentPreview({
  rankedPlayers = [],
  lastMatchPlayers = [],
  teamNumber = 1,
  teamSize = 4,
} = {}) {
  const size = Math.max(1, Number(teamSize) || 4);
  const ranked = sortPlayersByRankingPoints(rankedPlayers);
  const start = (Math.max(1, Number(teamNumber) || 1) - 1) * size;
  const rankingSix = ranked.slice(start, start + 6);
  const lastFour = (lastMatchPlayers || [])
    .filter((p) => p && (p.name || p.display_name || p.user_id || p.player_id))
    .slice(0, size);
  let titular;
  let extras;
  let source;
  if (lastFour.length) {
    titular = padTitularFromRanking(lastFour, rankingSix, size);
    extras = excludePreviewPlayers(rankingSix, titular).slice(0, 2);
    source = "last_match";
  } else if (rankingSix.length) {
    titular = rankingSix.slice(0, size);
    extras = rankingSix.slice(size, 6);
    source = "ranking";
  } else {
    titular = [];
    extras = [];
    source = "ranking";
  }
  return {
    source,
    titular,
    extras,
  };
}

export function fixtureIsPast(matchDate, today) {
  const date = String(matchDate || "").slice(0, 10);
  const day = String(today || "").slice(0, 10);
  return Boolean(date && day && date < day);
}

export function matchPresenceLocked({ matchDate, today, scoreComplete }) {
  return Boolean(scoreComplete || fixtureIsPast(matchDate, today));
}
